import {
  AlertCircle,
  CarFront,
  ChartColumn,
  ClipboardList,
  FileText,
  ListCheck,
  ListChecks,
  ParkingCircle,
  PhoneCall,
  Scale,
  TriangleAlert,
  Warehouse,
  Waves,
} from "lucide-react";

import type {
  DashboardKpiKey,
  DashboardPeriodKey,
  DashboardPropertyTier,
} from "../types";

export const DASHBOARD_PERIOD_OPTIONS: {value: DashboardPeriodKey; label: string}[] = [
  {value: "today", label: "Today"},
  {value: "yesterday", label: "Yesterday"},
  {value: "last_7_days", label: "Last 7 days"},
  {value: "last_28_days", label: "Last 28 days"},
  {value: "last_30_days", label: "Last 30 days"},
  {value: "last_90_days", label: "Last 90 days"},
  {value: "month_to_date", label: "Month to date"},
  {value: "year_to_date", label: "Year to date"},
];

export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriodKey = "last_7_days";

/** Visual metadata per KPI key — keeps `kpi-card-*` files thin. */
export const KPI_METADATA: Record<
  DashboardKpiKey,
  {label: string; icon: React.ElementType; route?: string}
> = {
  dars: {label: "DARs", icon: FileText, route: "/dars"},
  safelistings: {label: "Safelistings", icon: ListCheck, route: "/safelistings"},
  citations: {label: "Citations", icon: Scale, route: "/citations"},
  permanent_permits: {
    label: "Permanent permits",
    icon: ParkingCircle,
    route: "/permanent-parking",
  },
  stored_vehicles: {
    label: "Stored Vehicles",
    icon: Warehouse,
    route: "/stored-vehicles",
  },
  incident_reports: {label: "Incidents", icon: TriangleAlert, route: "/incidents"},
  amenity_access_logs: {label: "Amenity access", icon: Waves, route: "/poolaccesslogs"},
  dispatch_logs: {label: "Dispatch", icon: PhoneCall, route: "/dispatchlogs"},
  report_requests: {
    label: "Report requests",
    icon: ChartColumn,
    route: "/reportrequests",
  },
};

/**
 * Display order for activity KPIs — mirrors product priority
 * (DAR + parking enforcement first, then incidents, etc.).
 */
export const KPI_DISPLAY_ORDER: DashboardKpiKey[] = [
  "dars",
  "safelistings",
  "citations",
  "permanent_permits",
  "stored_vehicles",
  "incident_reports",
  "amenity_access_logs",
  "dispatch_logs",
];

export const PROPERTY_TIER_LABELS: Record<DashboardPropertyTier, string> = {
  paid: "Paid",
  free_limited: "Free (limited)",
  free_inspection: "Free (inspection)",
};

export const PROPERTY_TIER_ORDER: DashboardPropertyTier[] = [
  "paid",
  "free_limited",
  "free_inspection",
];

export const DAR_SEVERITY_ORDER: ("red" | "yellow" | "green")[] = [
  "red",
  "yellow",
  "green",
];

export const DAR_SEVERITY_LABELS = {
  red: "Red",
  yellow: "Yellow",
  green: "Green",
} as const;

/** Stable, high-contrast palette for dashboard time-series charts. */
export const TIME_SERIES_COLORS = [
  "oklch(0.58 0.19 253)",
  "oklch(0.62 0.17 145)",
  "oklch(0.70 0.16 75)",
  "oklch(0.62 0.20 25)",
  "oklch(0.58 0.18 305)",
  "oklch(0.64 0.15 205)",
  "oklch(0.56 0.18 15)",
  "oklch(0.55 0.15 120)",
] as const;

/** Icon registry — re-exported so management cards stay declarative. */
export const ManagementIcons = {
  officers: ClipboardList,
  properties: CarFront,
  shifts: ListChecks,
  alert: AlertCircle,
} as const;

/** Format a delta percentage for display. */
export function formatDeltaPct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

/** Convert backend's local bucket label (`YYYY-MM-DD`) to a short tick label. */
export function formatBucketTick(bucket: string): string {
  const parts = bucket.split("-");
  if (parts.length !== 3) return bucket;
  const date = new Date(`${bucket}T00:00:00`);
  if (Number.isNaN(date.getTime())) return bucket;
  return date.toLocaleDateString("en-US", {month: "short", day: "numeric"});
}

/** Long form of `formatBucketTick` for tooltip headers. */
export function formatBucketLong(bucket: string): string {
  const date = new Date(`${bucket}T00:00:00`);
  if (Number.isNaN(date.getTime())) return bucket;
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export {getDashboardOverviewQueryKey} from "./query-keys";
